import { z } from "zod";
import { JSON_SCHEMA_UUID, type JsonSchemaObject } from "./json-schema.ts";
import { PersonRef } from "./people.ts";

export const OracleAccessConfig = z.object({ includeOwnersInWebhook: z.boolean().default(false) }).strict();
export type OracleAccessConfig = z.infer<typeof OracleAccessConfig>;

const Identifier = z
  .string()
  .uuid()
  .transform((value) => value.toLowerCase());
export const OracleAccessSelection = z.object({ servicePrincipalId: Identifier, appRoleId: Identifier }).strict();
export type OracleAccessSelection = z.infer<typeof OracleAccessSelection>;

export const ORACLE_ACCESS_TARGET_VALUE_JSON_SCHEMA: JsonSchemaObject = {
  type: "object",
  properties: { servicePrincipalId: JSON_SCHEMA_UUID, appRoleId: JSON_SCHEMA_UUID },
  required: ["servicePrincipalId", "appRoleId"],
  additionalProperties: false,
};

const OracleAccessWireSelection = OracleAccessSelection.extend({ version: z.literal(1) }).strict();
const OracleAccessBrowser = z.object({ id: z.string().min(1).max(1_000), label: z.string().trim().min(1).max(1_000).optional() }).strict();

export function encodeOracleAccessSelection(selection: OracleAccessSelection): { id: string } {
  return OracleAccessBrowser.parse({ id: JSON.stringify({ version: 1, ...OracleAccessSelection.parse(selection) }) });
}

export function decodeOracleAccessSelection(value: unknown): OracleAccessSelection {
  const browser: { id: string } = OracleAccessBrowser.parse(value);
  const wire: z.infer<typeof OracleAccessWireSelection> = OracleAccessWireSelection.parse(JSON.parse(browser.id));
  return { servicePrincipalId: wire.servicePrincipalId, appRoleId: wire.appRoleId };
}

const RoleMemberTypes = z
  .array(z.enum(["User", "Application"]))
  .min(1)
  .max(2)
  .refine((types) => new Set(types).size === types.length);
const RoleText = z.string().trim().min(1).max(1_000);

export const OracleAccessMaterialized = OracleAccessSelection.extend({
  provider: z.literal("oracle"),
  servicePrincipalName: RoleText,
  databaseName: RoleText,
  appRoleValue: RoleText,
  appRoleName: RoleText,
  allowedMemberTypes: RoleMemberTypes,
  connectRoleId: Identifier,
  connectRoleValue: RoleText,
  connectAllowedMemberTypes: RoleMemberTypes,
  owners: z.array(PersonRef),
  ownersResolvedAt: z.string().datetime(),
}).strict();
export type OracleAccessMaterialized = z.infer<typeof OracleAccessMaterialized>;
